/**
 * The 9 missing Rail 2 domain index pages per M3X.REPORTS §0.8.
 *
 * Each is a thin wrapper around <ReportsDomainIndex> that supplies
 * its localized title + description. The catalog (lib/reports/
 * catalog.ts) is the data source — once per-domain PRs (4-13) add
 * their ReportManifests, every entry here automatically populates.
 *
 * The 4 existing /reports/{loans,leaves,eos,travel} pages stay on
 * their legacy components for back-compat; per-domain PRs migrate
 * each one over when ready.
 */

import { useTranslation } from "react-i18next";

import { ReportsDomainIndex } from "../../components/ui/ReportsDomainIndex";

export function PayrollReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="payroll"
      title={t("reports.domain_payroll_title", "Payroll reports")}
      description={t(
        "reports.domain_payroll_desc",
        "20 reports covering period summary, YTD statements, variance, GOSI, WPS, and bank file reconciliation.",
      )}
    />
  );
}

export function ExcusesReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="excuses"
      title={t("reports.domain_excuses_title", "Excuses reports")}
      description={t(
        "reports.domain_excuses_desc",
        "8 reports on excuse quota usage, common reasons, pattern detection, and approval rates by manager.",
      )}
    />
  );
}

export function AttendanceReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="attendance"
      title={t("reports.domain_attendance_title", "Attendance reports")}
      description={t(
        "reports.domain_attendance_desc",
        "12 reports on late arrivals, overtime, shift adherence, absenteeism, and Article 105 compliance.",
      )}
    />
  );
}

export function CompReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="comp"
      title={t("reports.domain_comp_title", "Compensation reports")}
      description={t(
        "reports.domain_comp_desc",
        "10 reports covering salary distribution, compa-ratio, Article 60 consent register, pay equity, and merit cycle summary.",
      )}
    />
  );
}

export function EmployeeReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="employee"
      title={t("reports.domain_employee_title", "Employee reports")}
      description={t(
        "reports.domain_employee_desc",
        "8 reports — master roster, probation pipeline, iqama expiry forecast, birthday + anniversary, nationality + gender + age mix.",
      )}
    />
  );
}

export function OrgReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="org"
      title={t("reports.domain_org_title", "Org reports")}
      description={t(
        "reports.domain_org_desc",
        "6 reports — headcount by org unit, span of control, vacancy report, position fill rate, org-chart depth, department cost.",
      )}
    />
  );
}

export function AnalyticsReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="analytics"
      title={t("reports.domain_analytics_title", "Cross-domain analytics")}
      description={t(
        "reports.domain_analytics_desc",
        "18 cross-domain analytics narrated by Sonnet — headcount evolution, turnover, diversity, total reward cost, manager effectiveness.",
      )}
    />
  );
}

export function ComplianceReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="compliance"
      title={t("reports.domain_compliance_title", "Compliance reports")}
      description={t(
        "reports.domain_compliance_desc",
        "14 KSA Labor Law audits — Articles 90 / 60 / 53 / 75 / 80 / 84-85 / 105 / 107 / 109 / 117 / 151-152 + CCHI + GOSI + iqama.",
      )}
    />
  );
}

export function AgentsReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="agents"
      title={t("reports.domain_agents_title", "AI / Agent reports")}
      description={t(
        "reports.domain_agents_desc",
        "10 reports over the M3X.1 v2 agent reasoning store — decision frequency, confidence drift, auto-resolve rate, Mutaba'aa veto frequency, AI brief engagement.",
      )}
    />
  );
}

// ===========================================================================
// 5 hotfix landings — replace legacy *ReportsPage components (M3X.REPORTS
// hotfix: domain index consistency). Each renders the same searchable
// card grid as /reports/excuses → ExcusesReportsLandingPage.
// ===========================================================================

export function LeavesReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="leaves"
      title={t("reports.domain_leaves_title", "Leaves reports")}
      description={t(
        "reports.domain_leaves_desc",
        "15 reports on balance, accruals, taken vs entitled, expiring leaves, and statutory floor compliance.",
      )}
    />
  );
}

export function LoansReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="loans"
      title={t("reports.domain_loans_title", "Loans reports")}
      description={t(
        "reports.domain_loans_desc",
        "Reports on active loans, repayment progress, Article 90 deduction-cap firings, and settlement events.",
      )}
    />
  );
}

export function EosReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="eos"
      title={t("reports.domain_eos_title", "End-of-Service reports")}
      description={t(
        "reports.domain_eos_desc",
        "Reports on EOS accruals, terminations in flight, settlement variance, and statutory Article 84/85 floor compliance.",
      )}
    />
  );
}

export function TravelReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="travel"
      title={t("reports.domain_travel_title", "Travel reports")}
      description={t(
        "reports.domain_travel_desc",
        "Reports on active travel, per-diem reconciliation, receipt compliance, and ticket budget usage.",
      )}
    />
  );
}

export function BenefitsReportsLandingPage() {
  const { t } = useTranslation();
  return (
    <ReportsDomainIndex
      domain="benefits"
      title={t("reports.domain_benefits_title", "Benefits reports")}
      description={t(
        "reports.domain_benefits_desc",
        "25 reports covering enrollment, claims, CCHI compliance, beneficiaries, and renewal pricing.",
      )}
    />
  );
}
